// ---------------------------------------------------------------------------
// Thin Tauri bridge. We access the global injected by Tauri rather than taking a
// hard dependency on @tauri-apps/api, so the same bundle runs in a plain browser
// (where isTauri() === false) and in the packaged desktop app.
//
// codexExec() supports:
//  - cancellation (kills the CLI child process via codex_cancel / /api/codex_cancel),
//  - streamed stdout (Tauri event channel / NDJSON chunked bridge response), fed
//    into the global activity tracker so the UI can show live progress.
// ---------------------------------------------------------------------------

import { beginCodexActivity, endCodexActivity, setCodexActivityTail } from './activity';
import { newId } from '../utils/id';

interface TauriEventApi {
  listen?: (
    event: string,
    handler: (e: { payload: unknown }) => void,
  ) => Promise<() => void>;
}

interface TauriGlobal {
  core?: { invoke: (cmd: string, args?: Record<string, unknown>) => Promise<unknown> };
  invoke?: (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;
  event?: TauriEventApi;
}

export function isTauri(): boolean {
  return typeof window !== 'undefined' && '__TAURI__' in window;
}

function tauriGlobal(): TauriGlobal {
  return (window as unknown as { __TAURI__: TauriGlobal }).__TAURI__;
}

export async function invoke<T = unknown>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  if (!isTauri()) throw new Error('Tauri APIs are only available in the desktop build.');
  const tauri = tauriGlobal();
  const fn = tauri.core?.invoke ?? tauri.invoke;
  if (!fn) throw new Error('Tauri invoke is unavailable.');
  return (await fn(cmd, args)) as T;
}

/** Subscribe to a Tauri event. Returns an unlisten fn (no-op outside Tauri). */
export async function listen(
  event: string,
  handler: (payload: unknown) => void,
): Promise<() => void> {
  if (!isTauri()) return () => {};
  const listenFn = tauriGlobal().event?.listen;
  if (!listenFn) return () => {};
  return listenFn(event, (e) => handler(e.payload));
}

export interface CodexExecResult {
  ok: boolean;
  stdout: string;
  stderr: string;
  code: number;
}

// --- streamed stdout wiring (desktop) ---------------------------------------
// The Rust command emits `codex:stdout` events with { execId, line }. We keep a
// map of execId -> activityId so lines land on the right activity entry.

const activityByExec = new Map<string, number>();
let streamingInitialized = false;

function ensureDesktopStreamListener(): void {
  if (streamingInitialized || !isTauri()) return;
  streamingInitialized = true;
  void listen('codex:stdout', (payload) => {
    const p = payload as { execId?: string; line?: string };
    if (!p?.execId || !p.line) return;
    const activityId = activityByExec.get(p.execId);
    if (activityId !== undefined) setCodexActivityTail(activityId, p.line);
  });
}

/**
 * Run `codex exec`.
 *  - Desktop (Tauri): via the Rust command in src-tauri/src/lib.rs.
 *  - Web (served by the Vite dev/preview server): via the POST /api/codex_exec
 *    bridge in vite.config.ts, which spawns the CLI on the local machine.
 *
 * A statically-hosted remote build has neither, so the call surfaces a clear
 * error — a web page cannot reach a visitor's local CLI.
 */
export async function codexExec(params: {
  codexPath: string;
  model: string;
  prompt: string;
  workingDirectory: string;
  webSearch: boolean;
  timeoutSeconds: number;
  /** Codex `-c model_reasoning_effort` value (low|medium|high). */
  reasoningEffort?: string;
  /** Human label shown in the global "thinking" animation while this runs. */
  label?: string;
}): Promise<CodexExecResult> {
  const execId = newId('exec');
  let cancelled = false;

  const cancel = () => {
    cancelled = true;
    if (isTauri()) {
      void invoke('codex_cancel', { execId }).catch(() => {});
    } else {
      void fetch('/api/codex_cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ execId }),
      }).catch(() => {});
    }
  };

  // Bracket the whole call so the UI shows a thinking animation for as long as
  // Codex is generating — including the network/CLI round-trip in either build.
  const activityId = beginCodexActivity(params.label, cancel);
  activityByExec.set(execId, activityId);
  try {
    if (isTauri()) {
      ensureDesktopStreamListener();
      const res = await invoke<CodexExecResult>('codex_exec', {
        execId,
        codexPath: params.codexPath,
        model: params.model,
        prompt: params.prompt,
        workingDirectory: params.workingDirectory,
        webSearch: params.webSearch,
        reasoningEffort: params.reasoningEffort ?? '',
        timeoutSeconds: params.timeoutSeconds,
      });
      if (cancelled) return { ok: false, stdout: '', stderr: 'Cancelled.', code: -2 };
      return res;
    }

    // Web build: call the local Codex bridge served by Vite (streaming NDJSON).
    let res: Response;
    try {
      res = await fetch('/api/codex_exec', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...params, execId, stream: true }),
      });
    } catch (e) {
      throw new Error(
        `Could not reach the local Codex bridge at /api/codex_exec. ` +
          `The web build runs Codex through the Vite dev/preview server — start the app with \`npm run dev\` ` +
          `on a machine that has the Codex CLI. (${(e as Error).message})`,
      );
    }

    const contentType = res.headers.get('content-type') ?? '';
    if (contentType.includes('application/x-ndjson') && res.body) {
      // Streaming path: NDJSON lines {t:'out',d:line} … then a final {t:'res',r:result}.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let result: CodexExecResult | null = null;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, nl).trim();
          buffer = buffer.slice(nl + 1);
          if (!line) continue;
          try {
            const msg = JSON.parse(line) as { t: string; d?: string; r?: CodexExecResult };
            if (msg.t === 'out' && msg.d) setCodexActivityTail(activityId, msg.d);
            else if (msg.t === 'res' && msg.r) result = msg.r;
          } catch {
            /* ignore malformed stream lines */
          }
        }
      }
      if (cancelled) return { ok: false, stdout: '', stderr: 'Cancelled.', code: -2 };
      if (result) return result;
      throw new Error('The Codex bridge stream ended without a result.');
    }

    const text = await res.text();
    try {
      return JSON.parse(text) as CodexExecResult;
    } catch {
      throw new Error(
        'The Codex bridge is not available (got a non-JSON response). It only works when the app is served by ' +
          'the Vite dev/preview server on a machine with the Codex CLI — not from a statically-hosted build.',
      );
    }
  } finally {
    activityByExec.delete(execId);
    endCodexActivity(activityId);
  }
}

// --- Lightweight availability check ------------------------------------------

export interface CodexVersionResult {
  ok: boolean;
  /** e.g. "codex-cli 0.139.0" when ok; an error description otherwise. */
  detail: string;
}

/**
 * Cheap "is the Codex CLI installed and launchable?" probe (`codex --version`).
 * Unlike testConnection() this makes no model call, so it's fast and free —
 * used for the AI-status chip and the onboarding wizard.
 */
export async function codexVersion(codexPath: string): Promise<CodexVersionResult> {
  try {
    if (isTauri()) {
      return await invoke<CodexVersionResult>('codex_version', { codexPath });
    }
    const res = await fetch('/api/codex_version', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ codexPath }),
    });
    const text = await res.text();
    return JSON.parse(text) as CodexVersionResult;
  } catch (e) {
    return {
      ok: false,
      detail: `Could not check the Codex CLI: ${(e as Error).message}`,
    };
  }
}
